import {
  Button, Card, CardActions, CardHeader, Divider, Typography,
} from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { PlusCircle } from '../../../../icons/plus-circle';
import STRING_KEYS from '../../../../language/keys';
import { Anchor } from '../../../../components/Anchor';
import { VIEW_MODE } from '../../../../consts/enums';
import TalentExperienceItem from './TalentExperienceItem';
import { style } from './style';

type TalentExperienceProps = {
  employmentHistory: EmplymentHistoryWithId[],
  viewMode: ViewMode,
  onExperienceSelect?: (select: UpdateValues<string | boolean | null | DateObject>,
     id:string) => void,
  onAddNewExperienceChapter?: () => void,
  onDeleteExperianceChapter?: (id:string) => void,
  disabled?: boolean,
  onOpenCreateNewCompanyModal?: (id:string) => void,
}

const TalentExperience = ({
  employmentHistory,
  onExperienceSelect = () => { /* noop */ },
  onAddNewExperienceChapter,
  onDeleteExperianceChapter,
  onOpenCreateNewCompanyModal,
  disabled,
  viewMode,
}: TalentExperienceProps):JSX.Element => {
  const { t: translate } = useTranslation();

  const canAdd = typeof onAddNewExperienceChapter === 'function' && viewMode === VIEW_MODE.EDITABLE;

  const cardStyle = {
    ...style.card.style,
    ...(disabled) && style.cardDisabled,
  };

  return (
    <Card sx={ style.card.sx } style={ cardStyle }>
      <Anchor id="experience" />
      <CardHeader title={ (
        <Typography
          variant={ style.card.cardHeader.typography.variant }
          color={ style.card.cardHeader.typography.color }
        >
          {translate(STRING_KEYS.TITLE_EXPERIENCE)}
        </Typography>
      ) }
      />
      <Divider />
      { employmentHistory.map((employment:EmplymentHistoryWithId) => {
        return (
          <TalentExperienceItem
            key={ employment.id }
            startDate={ employment.startDate }
            endDate={ employment?.endDate }
            companyName={ employment.companyName }
            companyLogo={ employment.companyLogo }
            role={ employment.title || '' }
            placeholderTitle={ translate(STRING_KEYS.TITLE_ROLE) }
            badges={ employment.badges }
            onDataValueChanged={ onExperienceSelect }
            companyId={ employment.companyId }
            disabled={ disabled }
            id={ employment.id }
            onDeleteBlock={ onDeleteExperianceChapter }
            onOpenCreateNewCompanyModal={ onOpenCreateNewCompanyModal }
            viewMode={ viewMode }
            keyInsights={ employment.keyInsights }
          />
        );
      })}

      { canAdd && (
        <CardActions>
          <Button
            onClick={ onAddNewExperienceChapter }
            startIcon={ <PlusCircle /> }
          >
            { translate(STRING_KEYS.ADD_NEW_CHAPTER_EXPERIENCE) }
          </Button>
        </CardActions>
      )}
    </Card>
  );
};

TalentExperience.defaultProps = {
  onExperienceSelect: () => { /* noop */ },
  onAddNewExperienceChapter: undefined,
  onDeleteExperianceChapter: undefined,
  disabled: false,
  onOpenCreateNewCompanyModal: undefined,
};

export default React.memo(TalentExperience);
